import { StateCreator } from 'zustand';
import type { IntakeQuizQuestion } from './schemas';

const INTAKE_QUIZ_START_SESSION = 'INTAKE_QUIZ_START_SESSION';
const INTAKE_QUIZ_ADVANCE = 'INTAKE_QUIZ_ADVANCE';
const INTAKE_QUIZ_GO_BACK = 'INTAKE_QUIZ_GO_BACK';
const INTAKE_QUIZ_COMPLETE = 'INTAKE_QUIZ_COMPLETE';
const INTAKE_QUIZ_RESET = 'INTAKE_QUIZ_RESET';

/**
 * One step on the intake-quiz back stack: the question that was answered
 * plus the codes the patient selected. Held so we can re-prefill the cards
 * when the user taps Back, and to keep client state consistent if the
 * server's response-pruning ever lets us re-send a different answer for
 * the same question.
 */
export type IntakeQuizHistoryEntry = {
  question: IntakeQuizQuestion;
  selectedCodes: readonly string[];
};

export interface IntakeQuizState {
  sessionId: string | null;
  currentQuestion: IntakeQuizQuestion | null;
  history: readonly IntakeQuizHistoryEntry[];
  /**
   * Selected codes that should pre-fill the option cards when
   * `currentQuestion` is rendered. Non-empty after `goBack()` (the user
   * returned to a previously-answered question and we want their cards
   * still checked); empty when the user reaches a question for the first
   * time.
   */
  prefillCodes: readonly string[];
  /** True once the quiz flow reaches `Ended: true`. Persisted so the loader
   * can redirect returning users past the quiz without re-running the flow. */
  completed: boolean;
}

export interface IntakeQuizActions {
  /** Record a freshly-created session and the entry-point question. */
  startSession: (
    sessionId: string,
    question: IntakeQuizQuestion,
  ) => void;
  /**
   * Push the just-answered question + its selected codes onto the history
   * stack and swap `currentQuestion` to the BE-returned next question.
   */
  advance: (
    answeredQuestion: IntakeQuizQuestion,
    selectedCodes: readonly string[],
    nextQuestion: IntakeQuizQuestion,
  ) => void;
  /**
   * Pop one history entry and surface it as `currentQuestion`. Returns the
   * popped entry so the page layer can re-prefill the option cards. Returns
   * `null` when the stack is empty (back button shouldn't have been visible).
   */
  goBack: () => IntakeQuizHistoryEntry | null;
  /**
   * Mark the flow finished by clearing `currentQuestion`. Keeps `history`
   * so the page layer can still report analytics counts. The DS
   * `<QuestionStep>` slides off when its `question` goes null — call this
   * (rather than `reset`) the moment the BE returns `Ended: true`.
   */
  complete: () => void;
  reset: () => void;
}

export type IntakeQuizSlice = {
  intakeQuiz: IntakeQuizState & IntakeQuizActions;
};

export const initialIntakeQuizState: IntakeQuizState = {
  sessionId: null,
  currentQuestion: null,
  history: [],
  prefillCodes: [],
  completed: false,
};

export const createIntakeQuizSlice: StateCreator<
  IntakeQuizSlice,
  [['zustand/devtools', never]]
> = (set, get) => ({
  intakeQuiz: {
    ...initialIntakeQuizState,
    startSession: (sessionId, question) =>
      set(
        (state) => ({
          intakeQuiz: {
            ...state.intakeQuiz,
            sessionId,
            currentQuestion: question,
            history: [],
            prefillCodes: [],
          },
        }),
        false,
        INTAKE_QUIZ_START_SESSION,
      ),
    advance: (answeredQuestion, selectedCodes, nextQuestion) =>
      set(
        (state) => ({
          intakeQuiz: {
            ...state.intakeQuiz,
            history: [
              ...state.intakeQuiz.history,
              { question: answeredQuestion, selectedCodes },
            ],
            currentQuestion: nextQuestion,
            prefillCodes: [],
          },
        }),
        false,
        INTAKE_QUIZ_ADVANCE,
      ),
    goBack: () => {
      const { history } = get().intakeQuiz;
      const popped = history[history.length - 1];
      if (!popped) return null;
      set(
        (state) => ({
          intakeQuiz: {
            ...state.intakeQuiz,
            history: state.intakeQuiz.history.slice(0, -1),
            currentQuestion: popped.question,
            prefillCodes: popped.selectedCodes,
          },
        }),
        false,
        INTAKE_QUIZ_GO_BACK,
      );
      return popped;
    },
    complete: () =>
      set(
        (state) => ({
          intakeQuiz: {
            ...state.intakeQuiz,
            currentQuestion: null,
            prefillCodes: [],
            completed: true,
          },
        }),
        false,
        INTAKE_QUIZ_COMPLETE,
      ),
    reset: () =>
      set(
        (state) => ({
          intakeQuiz: {
            ...state.intakeQuiz,
            ...initialIntakeQuizState,
          },
        }),
        false,
        INTAKE_QUIZ_RESET,
      ),
  },
});
